// Lib
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef } from 'react';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';

// Utils
import { navigateToUrl, navigateToBoard } from '../../reducers/navigationActions';
import { RerouteDeepLinkPlugin } from '../../../capacitor_plugins/rerouteDeepLink';
import { CAPACITOR_REROUTE_DEEP_LINK_PLUGIN } from '../../../capacitor_plugins/pluginConstants';
import { withPayloadLogging } from '../capacitorPayloadLogging';
import globalLogger from '../../logger';

// Selectors
import { getCurrentUserLastViewedBoardId, getCurrentUserRootBoardId } from '../../user/currentUserSelector';
import { getLocalLastNavigationId } from '../../user/navigationHistory/localNavigationHistoryService';

const parseUrl = (url: string): URL | null => {
    try {
        return new URL(url);
    } catch {
        return null;
    }
};

const isMilanoteAppHost = (hostname: string): boolean => {
    return hostname === 'app.milanote.com' || hostname === 'staging.milanote.com';
};

const HANDLED_URL_TTL_MS = 5000;
const logger = globalLogger.createChannel('capacitor.deepLinking');

// Check if the URL needs to be hit first and then use the resulting URL (i.e. for redirects)
const shouldResolveUrl = (url: string): boolean => {
    const parsedUrl = parseUrl(url);
    const hostname = parsedUrl?.hostname;

    return (
        url.includes('.milanote.com/api/auth/email/verify') || // email verification
        hostname === 'links.milanote.com' || // links in daily digest, notifications, etc
        hostname === 'comms.milanote.com' || // intercom branded links
        url.includes('account.milanote.com/subscription-invite#code=') // claim pro account
    );
};

const isBoardUrl = (url: string): boolean => {
    // The board ID is matched for 14 to 15 characters because there was some issue with element ID creation at some point
    // that caused ID creations with an extra character, which now exists in the database.
    // This regex also matches an optional permission ID ("p") query parameter.
    const boardUrlRegex =
        /^https:\/\/(app|staging)\.milanote\.com\/[a-zA-Z0-9]{14,15}(?:\/.*)?(?:\?p=[a-zA-Z0-9]{11})?\/?$/;
    return boardUrlRegex.test(url);
};

const isRootAppUrl = (url: string): boolean => {
    const parsedUrl = parseUrl(url);
    if (!parsedUrl) return false;
    if (!isMilanoteAppHost(parsedUrl.hostname)) return false;

    // Accept root URLs even when they include query/hash from wrapped links.
    return parsedUrl.pathname === '' || /^\/+$/.test(parsedUrl.pathname);
};

// Check if the URL should just be used as is.
const shouldDirectlyNavigate = (url: string): boolean => {
    return (
        url.includes('/change-password?code=') || // password reset
        isBoardUrl(url)
    );
};

export default function () {
    const dispatch = useDispatch();
    const lastViewedBoard = useSelector(getCurrentUserLastViewedBoardId);
    const rootBoardId = useSelector(getCurrentUserRootBoardId);
    const lastViewedBoardRef = useRef(lastViewedBoard);
    const rootBoardIdRef = useRef(rootBoardId);
    const handledUrlsRef = useRef(new Map<string, number>());

    useEffect(() => {
        lastViewedBoardRef.current = lastViewedBoard;
        rootBoardIdRef.current = rootBoardId;
    }, [lastViewedBoard, rootBoardId]);

    useEffect(() => {
        if (!Capacitor.isPluginAvailable('App')) return;
        let isEffectActive = true;
        let appUrlOpenListenerHandle: { remove: () => Promise<void> } | null = null;

        const pruneHandledUrls = () => {
            const now = Date.now();

            handledUrlsRef.current.forEach((expiresAt, handledUrl) => {
                if (expiresAt <= now) {
                    handledUrlsRef.current.delete(handledUrl);
                }
            });
        };

        const hasHandledUrl = (url: string) => {
            pruneHandledUrls();
            return handledUrlsRef.current.has(url);
        };

        const markUrlHandled = (...urls: string[]) => {
            pruneHandledUrls();

            const expiresAt = Date.now() + HANDLED_URL_TTL_MS;
            urls.forEach((handledUrl) => {
                handledUrlsRef.current.set(handledUrl, expiresAt);
            });
        };

        const navigateToLastOpenedBoard = () => {
            if (!isEffectActive) return false;

            // Navigate to last opened board when opening the root app URL
            // This mirrors the web app behavior from currentUserActions.js
            const localLastNavigation = getLocalLastNavigationId();

            const boardId = localLastNavigation || lastViewedBoardRef.current || rootBoardIdRef.current;

            if (boardId) {
                dispatch(navigateToBoard({ boardId, resetScroll: true, navigationSource: undefined }));
                return true;
            }

            return false;
        };

        const handleResolvedUrl = (resolvedUrl: string) => {
            if (isRootAppUrl(resolvedUrl)) {
                const hasNavigated = navigateToLastOpenedBoard();
                if (hasNavigated) {
                    markUrlHandled(resolvedUrl);
                }
                return hasNavigated;
            }

            if (!isEffectActive) return false;
            dispatch(navigateToUrl(resolvedUrl));
            markUrlHandled(resolvedUrl);
            return true;
        };

        const handleIncomingUrl = async (url: string) => {
            if (!isEffectActive || !url || hasHandledUrl(url)) return;

            if (shouldResolveUrl(url)) {
                if (!Capacitor.isPluginAvailable('CapacitorHttp')) return;
                try {
                    const response = await CapacitorHttp.get({ url });
                    if (!isEffectActive) return;

                    const wasHandled = handleResolvedUrl(response.url);
                    if (wasHandled) {
                        markUrlHandled(url);
                    }
                } catch (error) {
                    logger.warn('Failed to resolve deep link URL', { url }, error);
                }
                return;
            }

            if (isRootAppUrl(url)) {
                const wasHandled = navigateToLastOpenedBoard();
                if (wasHandled) {
                    markUrlHandled(url);
                }
                return;
            }

            if (shouldDirectlyNavigate(url)) {
                if (!isEffectActive) return;
                dispatch(navigateToUrl(url));
                markUrlHandled(url);
                return;
            }

            if (!Capacitor.isPluginAvailable(CAPACITOR_REROUTE_DEEP_LINK_PLUGIN)) return;
            try {
                if (!isEffectActive) return;

                await withPayloadLogging(
                    `${CAPACITOR_REROUTE_DEEP_LINK_PLUGIN}.openUrlInExternalBrowserWithoutLoopingBackToApp`,
                    (payload) => RerouteDeepLinkPlugin.openUrlInExternalBrowserWithoutLoopingBackToApp(payload),
                    { url },
                );

                if (!isEffectActive) return;
                markUrlHandled(url);
            } catch (error) {
                logger.warn('Failed to reroute deep link to external browser', { url }, error);
            }
        };

        App.getLaunchUrl()
            .then((launchUrl) => {
                if (!isEffectActive || !launchUrl?.url) return;
                void handleIncomingUrl(launchUrl.url);
            })
            .catch((error) => {
                logger.warn('Failed to get launch URL', error);
            });

        App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
            void handleIncomingUrl(event.url);
        })
            .then((listenerHandle) => {
                if (!isEffectActive) {
                    void listenerHandle.remove();
                    return;
                }

                appUrlOpenListenerHandle = listenerHandle;
            })
            .catch((error) => {
                logger.warn('Failed to attach appUrlOpen listener', error);
            });

        return () => {
            isEffectActive = false;

            if (appUrlOpenListenerHandle) {
                void appUrlOpenListenerHandle.remove();
            }
        };
    }, [dispatch]);
}
